/**
 * QuestionWizardComponent
 *
 * A dynamic form builder component that allows users to create, edit, and manage
 * a sequence of questions in the FeedMe Sales Panel application. This component
 * supports multiple question types and handles the complete lifecycle of question
 * management.
 *
 * Features:
 * - Support for multiple question types (multiple choice, open-ended, redirect)
 * - Dynamic form validation
 * - Real-time question management (add, edit, remove)
 * - Limit of 4 standard questions + 1 redirect question
 * - Responsive design with mobile-first approach
 * - Modern UI with visual feedback for question types
 * - Form state management and validation
 *
 * @example
 * ```html
 * <app-question-wizard
 *   [questions]="questionsList"
 *   (questionsChange)="handleQuestionsUpdate($event)"
 * >
 * </app-question-wizard>
 * ```
 */
import { Component, EventEmitter, Input, Output, ChangeDetectorRef, NgZone, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  Question,
  QuestionType,
  QUESTION_TYPE_LABELS,
  RedirectQuestion,
  CustomQuestion,
} from '../../../models/question.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-question-wizard',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: "./question-wizard.component.html",

})


export class QuestionWizardComponent implements OnInit{
  /** Array of questions currently managed by the wizard */
  @Input() questions: Question[] = [];

  /** Event emitter that fires when questions are added, edited, or removed */
  @Output() questionsChange = new EventEmitter<Question[]>();

  @Input() userId!: string;
  @Output() cancel = new EventEmitter<void>();

  /** Available question types excluding redirect type (21) */
  questionTypes: QuestionType[] = ['1', '2', '3', '4', '21'];


  /** Labels for different question types in Italian */
  QUESTION_TYPE_LABELS = QUESTION_TYPE_LABELS;

  /** ID of the question currently being edited, null if no question is being edited */
  editingQuestionQueryId: string | null = null;

  /** Form group for adding new questions */
  questionForm!: FormGroup;

  /** Form group for editing existing questions */
  editForm!: FormGroup;

  /** Form group for adding the final redirect question */
  urlForm!: FormGroup;



  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private http: HttpClient
  ) {
    this.initializeForms();

  }



  /**
   * Initializes all form groups with their respective validators
   */
  private initializeForms(): void {
    this.questionForm = this.fb.group({
      type: ['1', Validators.required],
      title: ['', Validators.required],
      responseType: [''] // ← aggiungilo se manca
    });

    this.editForm = this.fb.group({
      type: ['1', Validators.required],
      title: ['', Validators.required],
      url: ['']
    });

    this.urlForm = this.fb.group({
      title: ['', Validators.required],
      url: ['', Validators.required]
    });
  }

  /**
   * Generates a unique identifier for new questions
   * @returns A unique string ID
   */
  private generateUniqueId(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  /**
   * Selects a question type for a new question
   * @param type The type of question to select ('1' for multiple choice, '2' for open-ended)
   */
  selectType(type: QuestionType) {
    console.log("Tipo domanda selezionato:"+type)

    this.questionForm.get('type')?.setValue(type);

    // Pulizia dei campi precedenti
    this.questionForm.removeControl('responseType');
    this.questionForm.removeControl('url');

    if (type === '4') {
      this.questionForm.addControl('responseType', this.fb.control('', Validators.required));
    }

    if (type === '21') {
      this.questionForm.addControl('url', this.fb.control('', [Validators.required, Validators.pattern('https?://.+')]));
    }
    this.questionForm.patchValue({ type });
  }

  /**
   * Adds a new standard question to the list
   * Validates the form and emits the updated questions array
   */
  addQuestion() {
    if (this.questionForm.valid) {
      const newQuestion = {
        queryId: this.generateUniqueId(),
        ...this.questionForm.value
      };
      this.questions = [...this.questions, newQuestion];
      this.questionsChange.emit(this.questions);
      this.questionForm.reset();
      this.questionCountFromDb++; // 👈 AGGIUNTA QUESTA LINEA
    }
  }

  /**
   * Adds a redirect question (type '21') as the final question
   * Validates the URL form and emits the updated questions array
   */
  addUrlQuestion() {
    if (this.urlForm.valid) {
      const redirectQuestion: RedirectQuestion = {
        queryId: this.generateUniqueId(),
        type: '21',
        ...this.urlForm.value
      };
      this.questions = [...this.questions, redirectQuestion];
      this.questionsChange.emit(this.questions);
      this.urlForm.reset();
      this.questionCountFromDb++; // 👈 AGGIUNTA QUESTA LINEA
    }
  }

  /**
   * Removes a question from the list by its ID
   * @param queryId The unique identifier of the question to remove
   */
  removeQuestion(queryId: string) {
    this.questions = this.questions.filter(q => q.queryId !== queryId);
    this.questionsChange.emit(this.questions);
  }

  /**
   * Initiates the editing mode for a question
   * @param question The question to be edited
   */
  startEdit(question: Question) {
    this.editingQuestionQueryId = question.queryId;
    this.editForm.patchValue({
      type: question.type,
      title: question.title,
      url: 'url' in question ? question.url : ''
    });
  }

  /**
   * Selects a new type for the question being edited
   * @param type The new type to assign to the question
   */
  selectEditType(type: QuestionType) {
    this.editForm.patchValue({ type });
  }

  /**
   * Saves the changes made to a question
   * @param question The original question being edited
   */
  saveEdit(question: Question) {
    if (this.editForm.valid) {
      const updatedQuestion = {
        ...question,
        ...this.editForm.value
      };
      this.questions = this.questions.map(q =>
        q.queryId === question.queryId ? updatedQuestion : q
      );
      this.questionsChange.emit(this.questions);
      this.cancelEdit();
    }
  }

  /**
   * Cancels the current editing operation
   */
  cancelEdit() {
    this.editingQuestionQueryId = null;
    this.editForm.reset();
  }

  /**
   * Gets the appropriate placeholder text based on the selected question type
   * @returns A string with the placeholder text in Italian
   */
  getPlaceholder(): string {
    const type = this.questionForm.get('type')?.value;
    return type === '21'
      ? 'Inserisci il titolo della domanda'
      : 'Inserisci il testo della domanda';
  }

  async onSave() {
    this.addUrlQuestion();
    try {
      const payload = this.questions.map(q => ({
        QueryID: 0,
        Body: q.title,
        TypeID: q.type,
        Link: q.type === '21' ? (q as RedirectQuestion).url || '' : '',
        responseType: q.type === '4' ? (q as CustomQuestion).responseType || '' : '',
        Active: true,
        UserID: this.userId // 👈 aggiungi questo campo
      }));

      // 👉 Logga i dati in console invece di salvarli
      console.log('Payload delle domande da salvare:', payload);

      const response = await firstValueFrom(
        this.http.post(`${environment.apiUrl}/dashboard/addQuestions`, payload)
      );

      console.log('✅ Risposta del backend:', response);
      alert('Domande salvate con successo!');
     // this.cancel.emit(); // torna alla lista utenti
      window.location.href = '/admin/dashboard';
    } catch (error) {
      console.error('❌ Errore durante il salvataggio delle domande:', error);
      alert('Errore durante il salvataggio');
    }
  }

  questionCountFromDb: number = 0;
  fetchExistingQuestions() {
    console.log("verifico domande")
    this.http.get<{ count: number }>(`${environment.apiUrl}/dashboard/countQueries`, {
      params: { userID: this.userId }
    }).subscribe({
      next: (data) => {
        console.log(data.count)
        this.questionCountFromDb = data.count;
      },
      error: (err) => {
        console.error('Errore nel fetch delle domande:', err);
      }
    });
  }

  ngOnInit(): void {
    this.fetchExistingQuestions();
  }

}
